export const CANVAS_STATE_COMMENT_PREFIX = "CANVAS_STATE_V1:";

export const encodeCanvasState = (state) => {
  try {
    const json = JSON.stringify(state);
    const bytes = new TextEncoder().encode(json);
    let binary = "";
    bytes.forEach((b) => {
      binary += String.fromCharCode(b);
    });
    return btoa(binary);
  } catch (error) {
    console.error("Failed to encode canvas state:", error);
    return "";
  }
};

export const decodeCanvasState = (encoded) => {
  if (!encoded) return null;
  try {
    const binary = atob(encoded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch (error) {
    console.error("Failed to decode canvas state:", error);
    return null;
  }
};

const isBlackStrokeColor = (color = "") => {
  if (typeof color !== "string") return false;
  const normalized = color.replace(/\s+/g, "").toLowerCase();
  return (
    normalized === "black" ||
    normalized === "#000" ||
    normalized === "#000000" ||
    normalized === "rgb(0,0,0)" ||
    normalized === "rgba(0,0,0,1)"
  );
};

const extractRotation = (transform) => {
  if (!transform) return 0;
  const match = transform.match(/rotate\(([^)]+)deg\)/);
  return match ? parseFloat(match[1]) : 0;
};

const parseBorderStyle = (borderValue, borderColorValue, fallbackColor, fallbackWidth = 2) => {
  let strokeColor = fallbackColor;
  let strokeWidth = fallbackWidth;

  if (typeof borderColorValue === "string" && borderColorValue.trim()) {
    strokeColor = borderColorValue.trim();
  }

  if (typeof borderValue === "string" && borderValue.trim()) {
    const colorMatch = borderValue.match(
      /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]+\)|hsla?\([^)]+\)/i,
    );
    if (colorMatch?.[0]) {
      strokeColor = colorMatch[0];
    }

    const widthMatch =
      borderValue.match(/(\d+(?:\.\d+)?)px/i) ||
      borderValue.match(/(\d+(?:\.\d+)?)/);
    if (widthMatch?.[1]) {
      const parsedWidth = parseFloat(widthMatch[1]);
      if (Number.isFinite(parsedWidth) && parsedWidth >= 0) {
        strokeWidth = parsedWidth;
      }
    }
  }

  return { strokeColor, strokeWidth };
};

const resolveRootDimension = (rawValue, fallback) => {
  if (typeof rawValue !== "string") return fallback;

  const trimmed = rawValue.trim();
  if (!trimmed) return fallback;

  if (trimmed.endsWith("%")) {
    const percent = parseFloat(trimmed);
    if (Number.isFinite(percent) && percent > 0) {
      return (percent / 100) * fallback;
    }
    return fallback;
  }

  const absolute = parseFloat(trimmed);
  if (Number.isFinite(absolute) && absolute > 0) {
    return absolute;
  }

  return fallback;
};

/**
 * Parse saved template HTML into Konva-compatible canvas elements.
 * @returns {{ textElements: Array, shapes: Array, imageSize: { width: number, height: number } | null }}
 */
export const parseTemplateHtmlToCanvas = (templateHtml, imageSize = { width: 500, height: 500 }) => {
  const emptyResult = { textElements: [], shapes: [], imageSize: null };

  if (!templateHtml || !templateHtml.trim()) {
    return emptyResult;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(templateHtml, "text/html");
    const container = doc.body.firstElementChild || doc.body;
    const encodedCanvasState =
      container.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state") ||
      doc.querySelector("[data-canvas-state]")?.getAttribute("data-canvas-state");

    const commentMatch = templateHtml.match(
      new RegExp(`<!--\\s*${CANVAS_STATE_COMMENT_PREFIX}([A-Za-z0-9+/=]+)\\s*-->`),
    );
    const encodedFromComment = commentMatch?.[1] || "";

    const decodedCanvasState =
      decodeCanvasState(encodedCanvasState) || decodeCanvasState(encodedFromComment);

    if (
      decodedCanvasState &&
      Array.isArray(decodedCanvasState.textElements) &&
      Array.isArray(decodedCanvasState.shapes)
    ) {
      const savedBaseWidth = Number(decodedCanvasState.baseWidth) || 0;
      const savedBaseHeight = Number(decodedCanvasState.baseHeight) || 0;
      const resolvedImageSize =
        savedBaseWidth > 0 && savedBaseHeight > 0
          ? { width: savedBaseWidth, height: savedBaseHeight }
          : null;

      return {
        textElements: decodedCanvasState.textElements,
        shapes: decodedCanvasState.shapes,
        imageSize: resolvedImageSize,
      };
    }

    const rootWidth = resolveRootDimension(container.style?.width || "", imageSize.width);
    const rootHeight = resolveRootDimension(container.style?.height || "", imageSize.height);
    const parseBaseWidth = rootWidth;
    const parseBaseHeight = rootHeight;

    let resolvedImageSize = null;
    if (
      Number.isFinite(rootWidth) &&
      Number.isFinite(rootHeight) &&
      rootWidth > 0 &&
      rootHeight > 0
    ) {
      resolvedImageSize = { width: rootWidth, height: rootHeight };
    }

    const elements = container.querySelectorAll('div[style*="position: absolute"]');
    const parsedTexts = [];
    const parsedShapes = [];

    elements.forEach((el, index) => {
      const style = el.style;
      const left = parseFloat(style.left) || 0;
      const top = parseFloat(style.top) || 0;
      const width = parseFloat(style.width) || 0;
      const height = parseFloat(style.height) || 0;

      const bgColor = style.backgroundColor;
      const borderRadius = style.borderRadius;
      const border = style.border;
      const borderColor = style.borderColor;
      const hasSpan = el.querySelector("span") !== null;
      const displayFlex = style.display === "flex";
      const alignItems = style.alignItems;
      const justifyContent = style.justifyContent;

      const clipPath = style.clipPath || style.webkitClipPath;
      const hasClipPath = clipPath && clipPath.includes("polygon");

      let polygonPointCount = 0;
      if (hasClipPath) {
        const polygonMatch = clipPath.match(/polygon\(([^)]+)\)/);
        if (polygonMatch) {
          const pointsStr = polygonMatch[1];
          const pointsList = pointsStr.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
          polygonPointCount = pointsList.length;
        }
      }
      const isTriangle = hasClipPath && polygonPointCount === 3;
      const isStar = hasClipPath && polygonPointCount >= 10;

      const svgElement = el.querySelector("svg");
      const hasSVG = svgElement !== null;

      const isShape =
        hasSVG ||
        (bgColor &&
          (borderRadius === "50%" ||
            isTriangle ||
            isStar ||
            (width > 0 &&
              height > 0 &&
              (displayFlex ||
                alignItems === "center" ||
                justifyContent === "center" ||
                hasSpan ||
                !el.textContent.trim()))));

      if (isShape) {
        const span = el.querySelector("span");
        const text = span ? span.textContent : (el.textContent || "").trim();

        let x = left;
        let y = top;
        let w = width;
        let h = height;

        if (style.left.includes("%")) {
          x = (parseFloat(style.left) / 100) * parseBaseWidth;
        }
        if (style.top.includes("%")) {
          y = (parseFloat(style.top) / 100) * parseBaseHeight;
        }
        if (style.width.includes("%")) {
          w = (parseFloat(style.width) / 100) * parseBaseWidth;
        }
        if (style.height.includes("%")) {
          h = (parseFloat(style.height) / 100) * parseBaseHeight;
        }

        const widthIsPercent = style.width.includes("%");
        const heightIsPercent = style.height.includes("%");
        const widthPercentValue = parseFloat(style.width);
        const heightPercentValue = parseFloat(style.height);
        const hasEqualPercentDimensions =
          widthIsPercent &&
          heightIsPercent &&
          Number.isFinite(widthPercentValue) &&
          Number.isFinite(heightPercentValue) &&
          Math.abs(widthPercentValue - heightPercentValue) < 0.1;
        const isCircleShape =
          borderRadius === "50%" &&
          (Math.abs(w - h) < 5 || hasEqualPercentDimensions);

        if (svgElement) {
          const pathElement = svgElement.querySelector("path");
          if (pathElement) {
            const pathData = pathElement.getAttribute("d") || "";
            const viewBox = svgElement.getAttribute("viewBox");
            let viewBoxOffset = { x: 0, y: 0 };

            if (viewBox) {
              const vbParts = viewBox.split(/\s+/).map(parseFloat);
              if (vbParts.length >= 2) {
                viewBoxOffset.x = vbParts[0] || 0;
                viewBoxOffset.y = vbParts[1] || 0;
              }
            }

            const points = [];
            const pathCommands =
              pathData.match(/[ML][\s,]*(-?\d+(?:\.\d+)?)[\s,]*(-?\d+(?:\.\d+)?)/g) || [];
            pathCommands.forEach((cmd) => {
              const coords = cmd.match(/-?\d+(?:\.\d+)?/g);
              if (coords && coords.length >= 2) {
                const px = parseFloat(coords[0]) - viewBoxOffset.x;
                const py = parseFloat(coords[1]) - viewBoxOffset.y;
                points.push(px, py);
              }
            });

            if (pathData.includes("Z") || pathData.includes("z")) {
              if (points.length >= 8) {
                const centerX = x + w / 2;
                const centerY = y + h / 2;

                parsedShapes.push({
                  id: `arrow-${Date.now()}-${index}`,
                  x: centerX,
                  y: centerY,
                  points,
                  fill: pathElement.getAttribute("fill") || "#f59e0b",
                  stroke: pathElement.getAttribute("stroke") || "#d97706",
                  strokeWidth: parseFloat(pathElement.getAttribute("stroke-width")) || 2,
                  draggable: true,
                  rotation: extractRotation(style.transform),
                });
              }
            } else if (points.length >= 4) {
              const minPX = Math.min(...points.filter((_, i) => i % 2 === 0));
              const minPY = Math.min(...points.filter((_, i) => i % 2 === 1));
              const adjustedPoints = points.map((p, idx) => {
                if (idx % 2 === 0) return p - minPX;
                return p - minPY;
              });

              parsedShapes.push({
                id: `line-${Date.now()}-${index}`,
                x: x + minPX,
                y: y + minPY,
                points: adjustedPoints,
                fill: pathElement.getAttribute("fill") || "#000000",
                stroke: pathElement.getAttribute("stroke") || "#000000",
                strokeWidth: parseFloat(pathElement.getAttribute("stroke-width")) || 3,
                draggable: true,
                rotation: extractRotation(style.transform),
              });
            }
          }
        } else if (isCircleShape) {
          const parsedCircleDiameter = hasEqualPercentDimensions
            ? (widthPercentValue / 100) * parseBaseWidth
            : w;
          const radius = parsedCircleDiameter / 2 || 50;
          const circleBorder = parseBorderStyle(border, borderColor, "#dc2626", 2);
          const hasBlackCircleStroke = isBlackStrokeColor(circleBorder.strokeColor);
          parsedShapes.push({
            id: `circle-${Date.now()}-${index}`,
            x: x + radius,
            y: y + radius,
            radius,
            fill: bgColor || "#ef4444",
            stroke: hasBlackCircleStroke ? "transparent" : circleBorder.strokeColor,
            strokeWidth: hasBlackCircleStroke ? 0 : circleBorder.strokeWidth,
            draggable: true,
            text,
            textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
            textFontFamily: span ? span.style.fontFamily || "Arial" : "Arial",
            textFontStyle: span
              ? (span.style.fontStyle + " " + span.style.fontWeight).trim() || "normal"
              : "normal",
            textDecoration: span ? span.style.textDecoration || "" : "",
            textFill: span ? span.style.color || "#ffffff" : "#ffffff",
            rotation: extractRotation(style.transform),
          });
        } else if (borderRadius === "50%" && !isCircleShape) {
          const ellipseBorder = parseBorderStyle(border, borderColor, "#6d28d9", 2);
          parsedShapes.push({
            id: `ellipse-${Date.now()}-${index}`,
            x: x + w / 2,
            y: y + h / 2,
            radiusX: w / 2,
            radiusY: h / 2,
            fill: bgColor || "#8b5cf6",
            stroke: ellipseBorder.strokeColor,
            strokeWidth: ellipseBorder.strokeWidth,
            draggable: true,
            text,
            textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
            textFontFamily: span ? span.style.fontFamily || "Arial" : "Arial",
            textFontStyle: span
              ? (span.style.fontStyle + " " + span.style.fontWeight).trim() || "normal"
              : "normal",
            textDecoration: span ? span.style.textDecoration || "" : "",
            textFill: span ? span.style.color || "#ffffff" : "#ffffff",
            rotation: extractRotation(style.transform),
          });
        } else if (isStar) {
          const polygonMatch = clipPath.match(/polygon\(([^)]+)\)/);
          let starPoints = [];
          let starCenterX = x + w / 2;
          let starCenterY = y + h / 2;

          if (polygonMatch) {
            const pointsStr = polygonMatch[1];
            const percentPairs =
              pointsStr.match(/(\d+(?:\.\d{1,2})?)%[\s,]+(\d+(?:\.\d{1,2})?)%/g) || [];
            const pointsFromTopLeft = [];

            percentPairs.forEach((pair) => {
              const match = pair.match(/(\d+(?:\.\d+)?)%[\s,]+(\d+(?:\.\d+)?)%/);
              if (match) {
                const pxFromLeft = (parseFloat(match[1]) / 100) * w;
                const pyFromTop = (parseFloat(match[2]) / 100) * h;
                pointsFromTopLeft.push(pxFromLeft, pyFromTop);
              }
            });

            if (pointsFromTopLeft.length > 0) {
              const xs = pointsFromTopLeft.filter((_, i) => i % 2 === 0);
              const ys = pointsFromTopLeft.filter((_, i) => i % 2 === 1);
              const minPX = Math.min(...xs);
              const maxPX = Math.max(...xs);
              const minPY = Math.min(...ys);
              const maxPY = Math.max(...ys);
              const containerCenterX = (minPX + maxPX) / 2;
              const containerCenterY = (minPY + maxPY) / 2;

              starCenterX = x + containerCenterX;
              starCenterY = y + containerCenterY;

              for (let i = 0; i < pointsFromTopLeft.length; i += 2) {
                starPoints.push(
                  pointsFromTopLeft[i] - containerCenterX,
                  pointsFromTopLeft[i + 1] - containerCenterY,
                );
              }
            }
          }

          if (starPoints.length === 0) {
            const outerRadius = Math.min(w, h) / 2;
            const innerRadius = outerRadius / 2;
            const numPoints = 5;
            for (let i = 0; i < numPoints * 2; i++) {
              const angle = (i * Math.PI) / numPoints;
              const radius = i % 2 === 0 ? outerRadius : innerRadius;
              starPoints.push(Math.cos(angle) * radius, Math.sin(angle) * radius);
            }
          }

          const starBorder = parseBorderStyle(border, borderColor, "#f59e0b", 2);
          parsedShapes.push({
            id: `star-${Date.now()}-${index}`,
            x: starCenterX,
            y: starCenterY,
            points: starPoints,
            fill: bgColor || "#fbbf24",
            stroke: starBorder.strokeColor,
            strokeWidth: starBorder.strokeWidth,
            draggable: true,
            text,
            textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
            textFontFamily: span ? span.style.fontFamily || "Arial" : "Arial",
            textFontStyle: span
              ? (span.style.fontStyle + " " + span.style.fontWeight).trim() || "normal"
              : "normal",
            textDecoration: span ? span.style.textDecoration || "" : "",
            textFill: span ? span.style.color || "#ffffff" : "#ffffff",
            rotation: extractRotation(style.transform),
          });
        } else if (isTriangle) {
          const size = Math.max(w, h) || 80;
          const triangleBorder = parseBorderStyle(border, borderColor, "#059669", 2);

          parsedShapes.push({
            id: `triangle-${Date.now()}-${index}`,
            x: x + size / 2,
            y: y + size / 2,
            size,
            fill: bgColor || "#10b981",
            stroke: triangleBorder.strokeColor,
            strokeWidth: triangleBorder.strokeWidth,
            draggable: true,
            text,
            textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
            textFontFamily: span ? span.style.fontFamily || "Arial" : "Arial",
            textFontStyle: span
              ? (span.style.fontStyle + " " + span.style.fontWeight).trim() || "normal"
              : "normal",
            textDecoration: span ? span.style.textDecoration || "" : "",
            textFill: span ? span.style.color || "#ffffff" : "#ffffff",
            rotation: extractRotation(style.transform),
          });
        } else {
          const rectBorder = parseBorderStyle(border, borderColor, "#1e40af", 2);
          parsedShapes.push({
            id: `rect-${Date.now()}-${index}`,
            x,
            y,
            width: w || 100,
            height: h || 100,
            fill: bgColor || "#3b82f6",
            stroke: rectBorder.strokeColor,
            strokeWidth: rectBorder.strokeWidth,
            draggable: true,
            text,
            textFontSize: span ? parseFloat(span.style.fontSize) || 24 : 24,
            textFontFamily: span ? span.style.fontFamily || "Arial" : "Arial",
            textFontStyle: span
              ? (span.style.fontStyle + " " + span.style.fontWeight).trim() || "normal"
              : "normal",
            textDecoration: span ? span.style.textDecoration || "" : "",
            textFill: span ? span.style.color || "#ffffff" : "#ffffff",
            rotation: extractRotation(style.transform),
          });
        }
      } else {
        const text = el.textContent || "";
        const fontSize = parseFloat(style.fontSize) || 24;
        const fontFamily = style.fontFamily || "Arial";
        const fontStyle = (style.fontStyle + " " + style.fontWeight).trim() || "normal";
        const textDecoration = style.textDecoration || "";
        const color = style.color || "#000000";

        let x = left;
        let y = top;
        if (style.left.includes("%")) {
          x = (parseFloat(style.left) / 100) * parseBaseWidth;
        }
        if (style.top.includes("%")) {
          y = (parseFloat(style.top) / 100) * parseBaseHeight;
        }

        parsedTexts.push({
          id: `text-${Date.now()}-${index}`,
          x,
          y,
          text,
          fontSize,
          fontFamily,
          fontStyle,
          textDecoration,
          fill: color,
          draggable: true,
          rotation: extractRotation(style.transform),
        });
      }
    });

    return {
      textElements: parsedTexts,
      shapes: parsedShapes,
      imageSize: resolvedImageSize,
    };
  } catch (error) {
    console.error("Error parsing template HTML:", error);
    return emptyResult;
  }
};
